import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import { fetchWithCORSProxy, useWidgetTokenStore } from '@elfsight/lib-utils';
import {
  useEmitAPIEvent,
  useRegisterAPIService
} from '@elfsight/widget-js-api';
import type { StorageOperations } from '@elfsight/widget-utils';
import {
  storageOperations,
  useCache,
  useWindowWidth
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useAsyncEffect } from 'use-async-effect';
import { MOBILE_WIDTH, PLAYER_SERVICE_NAME } from '../../constants';
import {
  checkUrlIsOnlineExclusion,
  getCORSProxyLink,
  hmsToSecondsOnly,
  sendGoogleAnalyticsTrackStartedPlayingEvents
} from '../../helpers';
import ReactPlayer from '../../player';
import type { ExtendedAudioItem } from '../sources';
import { useSource } from '../sources';

const DEFAULT_TTL = 999999;

type StatusCachedTrack = 'pending' | 'success' | 'failed';

interface Progress {
  loaded?: number;
  loadedSeconds?: number;
  played?: number;
  playedSeconds?: number;
}

interface PlayerState {
  loading: boolean;
  progress: Progress;
  duration: number;
  playing: boolean;
  loopTrack: boolean;
  loopPlaylist: boolean;
  volume: number;
  showPlaylist: boolean;
  end: boolean;
  isLive: boolean;
  savedTime: number;
  savedUrl: string | null;
  showPlayer: boolean;
}

interface PlayerContextValue {
  progress: Progress;
  duration: number;
  playing: boolean;
  togglePlaying: (data: boolean) => void;
  next: () => void;
  prev: () => void;
  seekTo: (data: number) => void;
  loopTrack: boolean;
  loopPlaylist: boolean;
  setLoopTrack: (data: boolean) => void;
  setLoopPlaylist: (data: boolean) => void;
  volume: number;
  setVolume: (data: number) => void;
  setProgress: (data: Progress) => void;
  setDuration: (data: number) => void;
  showPlaylist: boolean;
  setShowPlaylist: (data: boolean) => void;
  loading: boolean;
  isLive: boolean;
  statusCachedTrack: StatusCachedTrack;
  setClosePlayer: () => void;
  showPlayer: boolean;
  currentItem?: ExtendedAudioItem;
  hasInteracted?: boolean;
}

const initialState: PlayerState = {
  loading: true,
  progress: {},
  duration: 0,
  playing: false,
  loopTrack: false,
  loopPlaylist: false,
  volume: 0.5,
  showPlaylist: true,
  end: false,
  isLive: false,
  savedTime: 0,
  savedUrl: null,
  showPlayer: false
};

const PlayerContext = createContext<PlayerContextValue>({
  ...initialState,
  statusCachedTrack: 'pending',
  togglePlaying: () => {},
  next: () => {},
  prev: () => {},
  seekTo: () => {},
  setLoopTrack: () => {},
  setLoopPlaylist: () => {},
  setVolume: () => {},
  setProgress: () => {},
  setDuration: () => {},
  setShowPlaylist: () => {},
  setClosePlayer: () => {}
});

export const usePlayer = () => useContext(PlayerContext);

const PLAYER_CONFIG = {
  file: {
    attributes: {
      preload: 'auto'
    }
  }
};

interface SetProgressAction {
  type: 'setProgress';
  payload: Progress;
}

interface SetBooleanAction {
  type:
    | 'setLoading'
    | 'togglePlaying'
    | 'setLoopTrack'
    | 'setLoopPlaylist'
    | 'setShowPlaylist'
    | 'setEnd'
    | 'setIsLive'
    | 'setShowPlayer';
  payload: boolean;
}

interface SetNumberAction {
  type: 'setDuration' | 'setVolume' | 'setSavedTime';
  payload: number;
}

interface SetSavedURL {
  type: 'setSavedUrl';
  payload: string | null;
}

export type PlayerAction =
  | SetProgressAction
  | SetBooleanAction
  | SetNumberAction
  | SetSavedURL;

function reducer(state: PlayerState, action: PlayerAction) {
  switch (action.type) {
    case 'setLoading':
      return { ...state, loading: action.payload };
    case 'setProgress':
      return { ...state, progress: action.payload };

    case 'setDuration':
      return { ...state, duration: action.payload };

    case 'togglePlaying':
      return { ...state, playing: action.payload };

    case 'setLoopTrack':
      return { ...state, loopTrack: action.payload };

    case 'setLoopPlaylist':
      return { ...state, loopPlaylist: action.payload };

    case 'setVolume':
      return { ...state, volume: action.payload };

    case 'setShowPlaylist':
      return { ...state, showPlaylist: action.payload };

    case 'setEnd':
      return { ...state, end: action.payload };

    case 'setIsLive':
      return { ...state, isLive: action.payload };

    case 'setSavedTime':
      return { ...state, savedTime: action.payload };

    case 'setSavedUrl':
      return { ...state, savedUrl: action.payload };

    case 'setShowPlayer':
      return { ...state, showPlayer: action.payload };

    default:
      throw new Error();
  }
}

const APIForcedStats = { stopAutoTrackSwitch: false };

export function PlayerProvider({ children }: PropsWithChildren) {
  const {
    autoplay,
    showPlaylist: showPlaylistSetting,
    showPlaylistOnMobile: showPlaylistOnMobileSetting,
    saveProgress,
    sourceType,
    loopByDefault,
    displayDeactivation,
    whenToShowAgain,
    editMode,
    repeatByDefault,
    widgetId,
    disablePreload,
    googleAnalytics,
    source
  } = useSettings();
  const widgetTokenStore = useWidgetTokenStore();
  const { currentItem, items, setCurrentItemId, currentItemId } = useSource();
  const cache = useCache();
  const width = useWindowWidth();

  const [hasInteracted, setHasInteracted] = useState(false);
  const [withCORS, setWithCORS] = useState(false);
  const [statusCachedTrack, setStatusCachedTrack] =
    useState<StatusCachedTrack>('pending');
  const [isLoadingCache, setIsLoadingCache] = useState(true);
  const lastTrackedTrackRef = useRef<string | null | undefined>(null);
  const playerRef = useRef<ReactPlayer>(null);
  const previousItemsRef = useRef<ExtendedAudioItem[] | undefined>();

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    loading,
    progress,
    duration,
    playing,
    loopTrack,
    loopPlaylist,
    volume,
    showPlaylist,
    end,
    isLive,
    savedTime,
    savedUrl,
    showPlayer
  } = state;

  const isMobileView = width <= MOBILE_WIDTH;
  const finalShowPlaylistSetting = isMobileView
    ? showPlaylistOnMobileSetting
    : showPlaylistSetting;

  const emitAPIEvent = useEmitAPIEvent();
  useRegisterAPIService(
    PLAYER_SERVICE_NAME,
    () => ({
      pause() {
        dispatch({ type: 'togglePlaying', payload: false });
      },

      play() {
        dispatch({ type: 'togglePlaying', payload: true });
      },

      stopAutoTrackSwitch() {
        APIForcedStats.stopAutoTrackSwitch = true;
      },

      stop() {
        dispatch({ type: 'togglePlaying', payload: false });
        dispatch({ type: 'setProgress', payload: {} });
        seekTo(0);
      },
      getTracks() {
        return source;
      }
    }),
    []
  );

  useEffect(() => {
    // setTimeout - костыль, убрать после релиза https://elfsight.atlassian.net/browse/WIDGETS-2999
    setTimeout(() => {
      emitAPIEvent('playerAvailable');
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seekTo = (num: number) => playerRef?.current?.seekTo(num, 'fraction');

  const next = useCallback(() => {
    dispatch({ type: 'setProgress', payload: {} });
    dispatch({ type: 'setDuration', payload: 0 });

    if (loopPlaylist && currentItemId === items.length - 1) {
      setCurrentItemId(0);
    } else {
      setCurrentItemId(currentItemId + 1);
    }
  }, [currentItemId, items, loopPlaylist, setCurrentItemId]);

  const playedEnough = useMemo(() => {
    return (progress.playedSeconds ?? 0) > 3 && sourceType !== 'radio';
  }, [progress.playedSeconds, sourceType]);

  const prev = useCallback(() => {
    if (playedEnough) {
      seekTo(0);
    } else if (loopPlaylist && currentItemId === 0) {
      setCurrentItemId(items.length - 1);
      dispatch({ type: 'setProgress', payload: {} });
      dispatch({ type: 'setDuration', payload: 0 });
    } else {
      setCurrentItemId(currentItemId - 1);
      dispatch({ type: 'setProgress', payload: {} });
      dispatch({ type: 'setDuration', payload: 0 });
    }
  }, [currentItemId, items, loopPlaylist, playedEnough, setCurrentItemId]);

  const onEnd = () => {
    if (end) {
      dispatch({ type: 'setEnd', payload: false });
      if (loopTrack) {
        seekTo(0);
      } else if (loopPlaylist && currentItemId < items.length - 1) {
        next();
      } else if (loopPlaylist && currentItemId === items.length - 1) {
        dispatch({ type: 'setProgress', payload: {} });
        dispatch({ type: 'setDuration', payload: 0 });
        setCurrentItemId(0);
      } else if (
        !loopPlaylist &&
        items.length &&
        currentItemId < items.length - 1
      ) {
        next();
      } else {
        onTogglePlaying(false);
      }
    }
  };

  const onProgress = (data: Progress) => {
    dispatch({ type: 'setProgress', payload: data });
  };

  const onReady = async () => {
    if (savedTime && saveProgress && currentItem?.link === savedUrl) {
      seekTo(savedTime);
    }

    window.dispatchesOfAllElfsightAudioPlayers =
      window.dispatchesOfAllElfsightAudioPlayers ?? {};
    window.dispatchesOfAllElfsightAudioPlayers[widgetId ?? ''] = dispatch;
  };

  useEffect(
    () => dispatch({ type: 'setLoopPlaylist', payload: !!loopByDefault }),
    [loopByDefault]
  );

  useEffect(
    () => dispatch({ type: 'setLoading', payload: !duration }),
    [duration]
  );

  useEffect(() => {
    dispatch({
      type: 'setShowPlaylist',
      payload: finalShowPlaylistSetting
    });
  }, [finalShowPlaylistSetting]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onEnd, [end]);

  useEffect(() => {
    if (!items) {
      return;
    }

    const savedItem = [...items].find((item) => item.link === savedUrl);
    const savedItemIndex = savedItem && [...items].indexOf(savedItem);
    if (savedItemIndex && saveProgress) {
      setCurrentItemId(savedItemIndex);
    }

    if (savedTime && saveProgress && currentItem?.link === savedUrl) {
      seekTo(savedTime);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [savedUrl, savedTime]);

  useEffect(() => {
    dispatch({
      type: 'setLoopPlaylist',
      payload: repeatByDefault === 'repeatPlaylist'
    });
    dispatch({
      type: 'setLoopTrack',
      payload: repeatByDefault === 'repeatTrack'
    });
  }, [repeatByDefault]);

  useEffect(() => {
    if (currentItem?.duration) {
      dispatch({
        type: 'setDuration',
        payload:
          typeof currentItem.duration === 'string'
            ? hmsToSecondsOnly(currentItem.duration)
            : currentItem.duration
      });
    }
  }, [currentItem?.duration, currentItem?.id]);

  useEffect(() => {
    const isLoading = !currentItem?.duration;
    dispatch({ type: 'setLoading', payload: isLoading });
  }, [currentItem?.duration, currentItem?.link]);

  useAsyncEffect(
    async (isMounted) => {
      if (sourceType !== 'radio' || !currentItem?.link) {
        return;
      }

      let isOnline;

      // Нужен чтоб прерывать бесконечные запросы на получение стримов
      const abortController = new AbortController();

      try {
        if (checkUrlIsOnlineExclusion(currentItem.link)) {
          isOnline = true;
          setWithCORS(false);
          return;
        }

        isOnline = (
          await fetch(currentItem.link, { signal: abortController.signal })
        ).ok;

        if (isOnline && isMounted()) {
          setWithCORS(false);
        }
      } catch (_error) {
        isOnline = (
          await fetchWithCORSProxy(currentItem.link, widgetTokenStore.get(), {
            signal: abortController.signal
          })
        ).ok;

        if (isOnline && isMounted()) {
          setWithCORS(true);
        }
      } finally {
        abortController.abort();

        if (isMounted()) {
          dispatch({ type: 'setIsLive', payload: !!isOnline });
        }
      }
    },
    [currentItem?.link, sourceType]
  );

  useAsyncEffect(async (isMounted) => {
    const mountedDispatch = (action: PlayerAction) => {
      if (isMounted()) {
        return dispatch(action);
      }
    };

    try {
      const savedTime = (await cache.getCache(
        'savedTime',
        DEFAULT_TTL
      )) as number;
      mountedDispatch({
        type: 'setSavedTime',
        payload: savedTime
      });
    } catch {
      mountedDispatch({ type: 'setSavedTime', payload: 0 });
    }

    try {
      const savedUrl = (await cache.getCache(
        'savedUrl',
        DEFAULT_TTL
      )) as string;
      mountedDispatch({ type: 'setSavedUrl', payload: savedUrl });
      setStatusCachedTrack('success');
    } catch {
      mountedDispatch({ type: 'setSavedUrl', payload: null });
      setStatusCachedTrack('failed');
    }

    try {
      const playerClosed = await cache.getCache(
        'playerClosed',
        whenToShowAgain * 3600
      );
      mountedDispatch({ type: 'setShowPlayer', payload: !playerClosed });
    } catch {
      mountedDispatch({ type: 'setShowPlayer', payload: true });
    }

    try {
      const lastTrackedTrackData = (await cache.getCache(
        'lastTrackedTrack',
        DEFAULT_TTL
      )) as string;
      if (isMounted()) {
        lastTrackedTrackRef.current = lastTrackedTrackData || null;
      }
    } catch {
      if (isMounted()) {
        lastTrackedTrackRef.current = null;
      }
    } finally {
      setIsLoadingCache(false);
    }
  }, []);

  useAsyncEffect(
    async (isMounted) => {
      if (!isMounted()) return;

      try {
        const savedUrl = (await cache.getCache(
          'savedUrl',
          DEFAULT_TTL
        )) as string;

        if (saveProgress) {
          items?.find((item, index) => {
            if (item?.link === savedUrl) {
              setCurrentItemId(index);
            }
          });
        }
      } catch {
        if (isMounted()) dispatch({ type: 'setSavedUrl', payload: null });
      }
    },
    [items]
  );

  useAsyncEffect(
    async (isMounted) => {
      if (progress && (progress.played ?? 0) >= 0) {
        if (!isMounted() || !currentItem || statusCachedTrack === 'pending') {
          return;
        }

        await cache.setCache('savedTime', progress.played);
        await cache.setCache('savedUrl', currentItem.link);
      }
    },
    [progress]
  );

  const playerURL = useMemo(() => {
    if (!withCORS || !currentItem) {
      return currentItem?.link;
    }

    return getCORSProxyLink(currentItem.link, widgetTokenStore.get());
  }, [withCORS, currentItem, widgetTokenStore]);

  const onClosePlayer = useCallback(() => {
    dispatch({ type: 'togglePlaying', payload: false });
    dispatch({ type: 'setShowPlayer', payload: false });

    if (!editMode) {
      cache.setCache('playerClosed', true);
    }
  }, [cache, editMode]);

  useEffect(() => {
    if (!editMode) return;

    const showingTimer = setTimeout(
      () => dispatch({ type: 'setShowPlayer', payload: true }),
      1000
    );

    return () => {
      clearTimeout(showingTimer);
    };
  }, [editMode, showPlayer]);

  // Отправка событий Google Analytics
  useEffect(() => {
    if (editMode || !googleAnalytics) return;

    const hasSameTracks =
      previousItemsRef.current && items === previousItemsRef.current;
    const shouldTrack =
      playing && currentItem && !isLoadingCache && hasSameTracks;

    const trackKey = `${widgetId}-${currentItem?.id}`;

    if (shouldTrack && lastTrackedTrackRef.current !== trackKey) {
      sendGoogleAnalyticsTrackStartedPlayingEvents({
        googleAnalytics,
        widgetId,
        artist: currentItem?.author,
        title: currentItem?.title
      });

      lastTrackedTrackRef.current = trackKey;
      cache.setCache('lastTrackedTrack', trackKey);
    }

    previousItemsRef.current = items;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, currentItem?.id, items, hasInteracted]);

  const onTogglePlaying = useCallback(
    (isPlaying: boolean) => {
      dispatch({ type: 'togglePlaying', payload: isPlaying });

      if (isPlaying) {
        const currentId = widgetId ?? '';
        for (const id in window.dispatchesOfAllElfsightAudioPlayers) {
          if (id !== currentId) {
            window.dispatchesOfAllElfsightAudioPlayers[id]({
              type: 'togglePlaying',
              payload: false
            });
          }
        }
      }
    },
    [widgetId]
  );

  const playerContextProviderValue = useMemo(
    () => ({
      playerRef,
      progress,
      duration,
      playing,
      currentItem,
      togglePlaying: (data: boolean) => onTogglePlaying(data),
      next,
      prev,
      seekTo,
      loopTrack,
      loopPlaylist,
      setLoopTrack: (data: boolean) =>
        dispatch({ type: 'setLoopTrack', payload: data }),
      setLoopPlaylist: (data: boolean) =>
        dispatch({ type: 'setLoopPlaylist', payload: data }),
      setVolume: (data: number) =>
        dispatch({ type: 'setVolume', payload: data }),
      volume,
      setProgress: (data: Progress) =>
        dispatch({ type: 'setProgress', payload: data }),
      setDuration: (data: number) =>
        dispatch({ type: 'setDuration', payload: data }),
      showPlaylist,
      setShowPlaylist: (data: boolean) =>
        dispatch({ type: 'setShowPlaylist', payload: data }),
      loading,
      isLive,
      statusCachedTrack,
      setClosePlayer: () => onClosePlayer(),
      showPlayer,
      hasInteracted
    }),
    [
      progress,
      duration,
      playing,
      currentItem,
      next,
      prev,
      loopTrack,
      loopPlaylist,
      volume,
      showPlaylist,
      loading,
      isLive,
      statusCachedTrack,
      showPlayer,
      onTogglePlaying,
      onClosePlayer,
      hasInteracted
    ]
  );

  const key = `elfsight-${widgetId}.clicked_play`;
  let storage: StorageOperations<string> | null;
  try {
    storage = storageOperations(key, localStorage);
  } catch {
    storage = null;
  }

  if (sourceType === 'rss' && disablePreload) {
    PLAYER_CONFIG.file = PLAYER_CONFIG.file ?? {};
    PLAYER_CONFIG.file.attributes = PLAYER_CONFIG.file.attributes ?? {};
    PLAYER_CONFIG.file.attributes.preload = 'none';
  }

  const isReadyPlayer = !isLoadingCache && currentItem?.link;

  return (
    <PlayerContext.Provider value={playerContextProviderValue}>
      {isReadyPlayer && (
        <ReactPlayer
          ref={playerRef}
          url={playerURL}
          playing={playing}
          autoplay={autoplay}
          volume={volume}
          muted={volume === 0}
          onProgress={onProgress}
          onDuration={(data: number) =>
            dispatch({ type: 'setDuration', payload: data })
          }
          onReady={onReady}
          onEnded={() => {
            emitAPIEvent('trackEnded');
            if (!APIForcedStats.stopAutoTrackSwitch) {
              dispatch({ type: 'setEnd', payload: true });
            } else {
              dispatch({ type: 'togglePlaying', payload: false });
            }
          }}
          onInteracted={() => setHasInteracted(true)}
          hasInteracted={hasInteracted}
          onTogglePlaying={onTogglePlaying}
          saveProgress={saveProgress}
          playsinline
          loop={
            !APIForcedStats.stopAutoTrackSwitch &&
            (loopTrack || (loopPlaylist && items.length === 1))
          }
          config={PLAYER_CONFIG}
          storage={storage}
          displayDeactivation={displayDeactivation}
        />
      )}
      {children}
    </PlayerContext.Provider>
  );
}
